import { useEffect, useRef, memo } from "react";
import lottie, { AnimationItem } from "lottie-web";
import { lottieMap } from "./lottieData";
import { applyBrandThemeToLottie } from "./lottieTheming";

interface LottieIconProps {
  iconId: string;
  isActive: boolean;
  className?: string;
}

export const LottieIcon = memo(
  ({ iconId, isActive, className = "" }: LottieIconProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const animRef = useRef<AnimationItem | null>(null);
    const wasActiveRef = useRef(false);

    useEffect(() => {
      if (!containerRef.current) return;

      const animationData = lottieMap[iconId];
      if (!animationData) return;

      // 挂载时按当前主题给选中态图标的 teal 渐变着色（非破坏性深拷贝）。
      const themedData = applyBrandThemeToLottie(animationData);

      animRef.current = lottie.loadAnimation({
        container: containerRef.current,
        renderer: "svg",
        loop: false,
        autoplay: false,
        animationData: themedData,
      });

      animRef.current.addEventListener("DOMLoaded", () => {
        if (isActive) {
          animRef.current?.goToAndPlay(0, true);
        } else {
          animRef.current?.goToAndStop(0, true);
        }
      });

      return () => {
        animRef.current?.destroy();
        animRef.current = null;
      };
    }, [iconId]);

    useEffect(() => {
      if (!animRef.current) return;

      if (isActive && !wasActiveRef.current) {
        animRef.current.goToAndPlay(0, true);
      } else if (!isActive && wasActiveRef.current) {
        animRef.current.goToAndStop(0, true);
      }
      wasActiveRef.current = isActive;
    }, [isActive]);

    return <div ref={containerRef} className={className} />;
  }
);

LottieIcon.displayName = "LottieIcon";
